<template>
  <SideBar :class="{ 'disable-last-pane-resize': !expandedNews }">
    <Splitpanes class="default-theme" horizontal @resize="handleResize">
      <Pane
        :size="panesSize.watchList.size"
        :min-size="panesSize.watchList.minSize"
      >
        <Watchlist :is-expanded-news="expandedNews" />
      </Pane>

      <Pane
        v-if="showTechnicalChart && !showPortfolioVisualization"
        :size="panesSize.symbol.size"
        :min-size="panesSize.symbol.minSize"
      >
        <ComponentState :fetch-status="symbolStatus">
          <template #loading>
            <SymbolVerticalSkeleton />
          </template>

          <template #error>
            <Error class="p-2" @retry="() => getSymbol()" />
          </template>

          <SymbolDetail
            vertical
            show-bubble
            symbol-detail-layout="sideBar"
            :symbol="symbol"
            :portfolio-symbols="portfolioSymbols"
          />
        </ComponentState>
      </Pane>

      <Pane :size="panesSize.news.size" :min-size="panesSize.news.minSize">
        <News v-model:expanded-news="expandedNews" />
      </Pane>
    </Splitpanes>
  </SideBar>

  <Main
    :class="{ 'disable-last-pane-resize': !expandedAssets }"
    class="overflow-x-auto"
  >
    <Splitpanes
      ref="container"
      class="default-theme"
      horizontal
      @resize="handleResizeLeft"
    >
      <Pane
        v-bind="showTechnicalChart ? panesSize.technical : panesSize.symbol"
        :class="{ '!h-0': showPortfolioVisualization }"
      >
        <Symbol
          :portfolio-symbols="portfolioSymbols"
          :symbol="symbol"
          :symbol-status="symbolStatus"
          :is-loading-symbol="isRevalidatingSymbol && !symbol"
          :isin="activeIsin"
          @handle-show-technical-chart="handleShowTechnicalChart"
          @handle-show-option-chain="handleShowOptionChain"
          @get-symbol="getSymbol"
        />
      </Pane>

      <Pane
        v-bind="panesSize.assets"
        :class="{ '!h-full': showPortfolioVisualization }"
      >
        <div
          v-if="showPortfolioVisualization"
          class="flex rounded-md bg-neutral-secondary px-1 h-full mx-1"
        >
          <PortfolioVisualization
            :portfolio-symbols="portfolioSymbols"
            @handle-portfolio-visualization="handlePortfolioVisualization"
          />
        </div>
        <!-- fix height and padding of tabtables -->
        <div v-else class="rounded-md bg-neutral-secondary h-full mx-1">
          <NTabtables
            :key="`${isOption && 'has-option'}-${
              embeddedPutOptionSymbols.length && 'has-embedded-put-option'
            }`"
            v-model="tabtable"
            auto-activate
            :class="
              !expandedAssets &&
              '[&_[id^=tabtables-panel-wrapper]]:!max-h-1.5 [&_[id^=tabtables-panel-wrapper]]:!py-0'
            "
          >
            <NTabtable
              v-gid="{
                module: 'portfolio',
                screen: 'home',
                section: 'assetsTabSection',
                component: 'portfolioTabButton',
              }"
              @click="() => updateExpandedAssetsSection(true)"
            >
              <PortfolioTable
                v-if="expandedAssets"
                :portfolio-symbols="portfolioSymbols"
                :portfolio-status="portfolioStatus"
              />

              <template #title="{ isActive }">
                <div class="flex items-center relative ml-2">
                  <NText
                    variant="caption2"
                    :weight="isActive ? 'bold' : 'medium'"
                  >
                    {{ t('modules.portfolio.title') }}
                  </NText>
                </div>
              </template>

              <template #append>
                <NPopper hover offset-y="5" placement="top" append-to-body>
                  <div
                    v-gid="{
                      module: 'portfolio',
                      screen: 'home',
                      section: 'assetsTabSection',
                      component: 'showPortfolioVisualizationButton',
                    }"
                    @click.stop="handlePortfolioVisualization"
                  >
                    <div>
                      <NegarExpandIcon
                        class="text-neutral-secondary"
                        size="20"
                      />
                    </div>
                  </div>

                  <template #content>
                    <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                      <NText
                        class="text-neutral-quaternary"
                        variant="caption2"
                        weight="medium"
                      >
                        {{ t('modules.portfolio.portfolioStatus') }}
                      </NText>
                    </div>
                  </template>
                </NPopper>
              </template>
            </NTabtable>

            <NTabtable
              v-if="isOption"
              v-gid="{
                module: 'option',
                screen: 'home',
                section: 'assetsTabSection',
                component: 'optionPositionTabButton',
              }"
              @click="() => updateExpandedAssetsSection(true)"
            >
              <template #title="{ isActive }">
                <div class="flex items-center relative">
                  <NText
                    variant="caption2"
                    :weight="isActive ? 'bold' : 'medium'"
                  >
                    {{ t('option.openPosition') }}
                  </NText>

                  <span
                    v-if="showOptionPositionsBadge"
                    class="bg-critical-primary w-1 h-1 rounded-full absolute top-0 -left-1"
                  />
                </div>
              </template>

              <PositionsTabContent />
            </NTabtable>

            <NTabtable
              v-gid="{
                module: 'portfolio',
                screen: 'home',
                section: 'assetsTabSection',
                component: 'orderTabButton',
              }"
              @click="() => updateExpandedAssetsSection(true)"
            >
              <template #title="{ isActive }">
                <div class="flex items-center relative">
                  <NText
                    variant="caption2"
                    :weight="isActive ? 'bold' : 'medium'"
                  >
                    {{ t('common.orders') }}
                  </NText>
                </div>
              </template>

              <OrdersTablePanel
                v-model:filter="orderFilter"
                :orders="orders"
                :is-error-loading-orders="isErrorLoadingOrders"
                :expanded-assets="expandedAssets"
                @get-orders="getOrders"
              />
            </NTabtable>

            <NTabtable
              v-gid="{
                module: 'orderTables',
                screen: 'home',
                section: 'summerizeOrderTab',
                component: 'summerizeOrderTableTab',
              }"
              @click="() => updateExpandedAssetsSection(true)"
            >
              <template #title="{ isActive }">
                <div class="flex items-center relative">
                  <NText
                    variant="caption2"
                    :weight="isActive ? 'bold' : 'medium'"
                  >
                    {{ t('orders.trade') }}</NText
                  >

                  <span
                    v-if="hasNewSummarize"
                    class="bg-critical-primary w-1 h-1 rounded-full absolute top-0 -left-1"
                  />
                </div>
              </template>

              <ComponentState :fetch-status="orderSummarizeStatus">
                <template #loading>
                  <TableSkeleton :row-number="10" :column-number="8" />
                </template>

                <template #error>
                  <Error @retry="getOrderSummarize" />
                </template>

                <SummarizeOrdersTable :items="summarizedOrders" />
              </ComponentState>
            </NTabtable>

            <NTabtable
              v-if="embeddedPutOptionSymbols.length"
              v-gid="{
                module: 'portfolio',
                screen: 'home',
                section: 'assetsTabSection',
                component: 'embeddedPutOptionTabButton',
              }"
              @click="() => updateExpandedAssetsSection(true)"
            >
              <template #title="{ isActive }">
                <div class="flex items-center relative">
                  <NText
                    variant="caption2"
                    :weight="isActive ? 'bold' : 'medium'"
                  >
                    {{
                      t('modules.portfolio.embeddedPutOption.tableTitle')
                    }}</NText
                  >

                  <span
                    class="bg-critical-primary w-1 h-1 rounded-full absolute top-0 -left-1"
                  />
                </div>
              </template>
              <EmbeddedPutOptionTable :symbols="embeddedPutOptionSymbols" />
            </NTabtable>

            <template #actions>
              <NegarMinusIcon
                v-if="expandedAssets"
                @click="() => updateExpandedAssetsSection(false)"
              />

              <NegarArrowUpIcon
                v-else
                @click="() => updateExpandedAssetsSection(true)"
              />
            </template>

            <template #footer>
              <div
                v-if="
                  (ordersResultState === PORTFOLIO_RESULT_STATE.error &&
                    tabtable === ASSETS_TAB_INDEX.orders) ||
                  (portfolioResultState === PORTFOLIO_RESULT_STATE.error &&
                    tabtable === ASSETS_TAB_INDEX.portfolio)
                "
                class="flex justify-start items-center rounded-md gap-1 px-2"
              >
                <div>
                  <NegarErrorLightIcon
                    class="text-critical-primary"
                    size="16"
                  />
                </div>
                <div>
                  <NText variant="overline" weight="medium">
                    {{ t('modules.portfolio.portfolioErrorStatus') }}
                  </NText>
                </div>
                <NegarRefreshIcon
                  class="cursor-pointer text-neutral-secondary"
                  size="16"
                  @click="
                    () => {
                      tabtable === ASSETS_TAB_INDEX.portfolio
                        ? getPortfolio()
                        : getOrders();
                    }
                  "
                />
              </div>
            </template>
          </NTabtables>
        </div>
      </Pane>
    </Splitpanes>
  </Main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Splitpanes, Pane } from 'splitpanes';
import {
  PORTFOLIO_RESULT_STATE,
  useGetEmbeddedPutOptionSymbolsQuery,
  useLivePortfolioQuery,
} from '@core/portfolio';
import { getTimeDifference, pxToPercent } from '@libs/utils';
import { queryStatus } from '@libs/use';
import { useFeatureFlag } from '@libs/feature-flag';
import {
  ORDER_FILTERS,
  useLiveOrdersQuery,
  useLiveOrderSummarizeQuery,
} from '@core/order';
import {
  NegarExpandIcon,
  NegarErrorLightIcon,
  NegarMinusIcon,
  NegarArrowUpIcon,
  NegarRefreshIcon,
} from '@negar/icons';
import { NPopper, NText } from '@negar/core';

import { useRoute, useRouter } from 'vue-router';
import {
  getSymbolIsin,
  useSyncedSymbolDetailInBrowserTitle,
} from '@core/symbol';
import { watchOnce } from '@vueuse/core';
import {
  DAY_PER_MILLISECONDS,
  useLiveOptionPositionsQuery,
} from '@core/option';

import { Watchlist } from '@/modules/watchlist';
import {
  SymbolDetail,
  Symbol,
  SALAM_ISIN,
  SymbolVerticalSkeleton,
} from '@/modules/symbol';
import { News } from '@/modules/news';
import {
  OrdersTablePanel,
  SummarizeOrdersTable,
  useOrderShortcuts,
} from '@/modules/order';
import { useUserSettings } from '@/modules/user';
import {
  PortfolioTable,
  PortfolioVisualization,
  EmbeddedPutOptionTable,
} from '@/modules/portfolio';
import { useActiveSymbolIsin, useLiveSymbolQuery } from '@/modules/symbol';
import { PositionsTabContent } from '@/modules/option';

import SideBar from '@/components/SideBar.vue';
import Main from '@/components/Main.vue';
import Error from '@/components/Error.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import ComponentState from '@/components/ComponentState.vue';

import eventBus from '@/composables/event/eventBus';
import usePaneResize from '@/composables/usePaneResize';

import {
  ASSETS_TAB_INDEX,
  HOME_PANE_LAYOUT_DESIGN_SYSTEM_SETTINGS,
  SETTLEMENT_DUE_TODAY_IN_DAYS,
} from './home/constants';

const showTechnicalChart = ref(false);
const tabtable = ref<number>(ASSETS_TAB_INDEX.portfolio);
const orderFilter = ref(ORDER_FILTERS.open.value);

const { settings, updateLayoutSettings } = useUserSettings();

const expandedAssets = ref(settings.value.layout?.expandedAssets ?? true);
const expandedNews = ref(settings.value.layout?.expandedNews ?? true);

const showPortfolioVisualization = ref(false);
const container = ref();

const router = useRouter();
const route = useRoute();

const isOption = useFeatureFlag('option');

const bottomSectionMinSize = computed(() =>
  pxToPercent(
    container.value?.$el?.clientHeight,
    HOME_PANE_LAYOUT_DESIGN_SYSTEM_SETTINGS.bottomSectionMinHeightPx
  )
);

const symbolContainerSectionHeight = computed(
  () => container.value?.$el?.clientHeight
);

const showOptionPositionsBadge = computed(() => {
  const todayStartTime = new Date(new Date().setHours(0, 0, 0, 0)).getTime();

  return Boolean(
    optionPositions.value.find((optionPosition) => {
      const isCashSettlementDay =
        getTimeDifference({
          startTime: todayStartTime,
          endTime:
            new Date(optionPosition.cashSettlementDate).getTime() +
            DAY_PER_MILLISECONDS,
        }).differenceInDays === SETTLEMENT_DUE_TODAY_IN_DAYS;
      const isPhysicalSettlementDay =
        getTimeDifference({
          startTime: todayStartTime,
          endTime:
            new Date(optionPosition.physicalSettlementDate).getTime() +
            DAY_PER_MILLISECONDS,
        }).differenceInDays === SETTLEMENT_DUE_TODAY_IN_DAYS;

      return isCashSettlementDay || isPhysicalSettlementDay;
    })
  );
});

const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

eventBus.on('openIndustryGroups', () => {
  if (showTechnicalChart.value) return;
  showTechnicalChart.value = true;
  expandedNews.value = false;
});

const {
  symbols: portfolioSymbols,
  isSuccessLoadingPortfolio,
  getPortfolio,
  isFetchingPortfolio,
  isErrorLoadingPortfolio,
  isSymbolLoaded,
  resultState: portfolioResultState,
} = useLivePortfolioQuery();

const portfolioStatus = computed(() => {
  if (isErrorLoadingPortfolio.value) return queryStatus.error;
  if (isSymbolLoaded.value) return queryStatus.success;

  return queryStatus.loading;
});

useOrderShortcuts(activeIsin);

const {
  orders,
  getOrders,
  isErrorLoadingOrders,
  result,
  resultState: ordersResultState,
} = useLiveOrdersQuery();

const { embeddedPutOptionSymbols } = useGetEmbeddedPutOptionSymbolsQuery();

const {
  summarizedOrders,
  getOrderSummarize,
  hasNewSummarize,
  orderSummarizeStatus,
} = useLiveOrderSummarizeQuery({
  immediate: computed(() => tabtable.value === ASSETS_TAB_INDEX.summarize),
});

const { symbol, getSymbol, symbolStatus, isRevalidatingSymbol } =
  useLiveSymbolQuery(activeIsin);

useSyncedSymbolDetailInBrowserTitle(symbol);

const { optionPositions } = useLiveOptionPositionsQuery();

const { panesSize, handleResize, handleResizeLeft } = usePaneResize({
  bottomSectionMinSize,
  expandedNews,
  expandedAssets,
  showTechnicalChart,
  symbolContainerSectionHeight,
});

const { t } = useI18n();

const handleShowTechnicalChart = (newValue: boolean, isExpanded: boolean) => {
  showTechnicalChart.value = newValue;

  expandedNews.value = settings.value.layout.expandedNews ?? !newValue;
  expandedAssets.value = settings.value.layout.expandedAssets ?? !isExpanded;
};

const handleShowOptionChain = (isOptionChain: boolean) => {
  if (isOptionChain) {
    panesSize.assets.size =
      HOME_PANE_LAYOUT_DESIGN_SYSTEM_SETTINGS.collapsedSize;
  }
};

const handlePortfolioVisualization = () => {
  showPortfolioVisualization.value = !showPortfolioVisualization.value;

  if (showPortfolioVisualization.value) {
    expandedNews.value = true;

    router.push({ query: { ...route.query, portfolio: 'true' } });
  } else {
    const newQuery = { ...route.query };

    expandedNews.value = settings.value.layout.expandedNews ?? true;

    delete newQuery.portfolio;
    delete newQuery.virtualPortfolio;

    router.push({ query: newQuery });
  }
};

function handleActiveSummarizeTab() {
  getOrderSummarize();

  hasNewSummarize.value = false;
}

const activeTabAction = computed(() => {
  const activeTabIndex = tabtable.value;

  if (activeTabIndex === ASSETS_TAB_INDEX.portfolio) return getPortfolio;

  if (activeTabIndex === ASSETS_TAB_INDEX.orders) {
    return getOrders;
  }

  if (activeTabIndex === ASSETS_TAB_INDEX.summarize) {
    return handleActiveSummarizeTab;
  }

  return () => {};
});

watch(activeTabAction, (callAction) => callAction());

watch(
  () => panesSize.assets.size,
  () => {
    if (
      panesSize.assets.size <
      panesSize.assets.minSize +
        HOME_PANE_LAYOUT_DESIGN_SYSTEM_SETTINGS.paddingForPanesSize
    ) {
      expandedAssets.value = false;
    } else {
      expandedAssets.value = true;
    }
  }
);

watch(
  () => panesSize.news.size,
  () => {
    if (
      panesSize.news.size <=
      panesSize.news.minSize +
        HOME_PANE_LAYOUT_DESIGN_SYSTEM_SETTINGS.paddingForPanesSize
    ) {
      expandedNews.value = false;
    } else {
      expandedNews.value = true;
    }
  }
);

const updateExpandedAssetsSection = (value?: boolean) => {
  updateLayoutSettings('expandedAssets', value ?? !expandedAssets.value);

  expandedAssets.value = value ?? !expandedAssets.value;
};

watchOnce(
  () => route,
  () => {
    if (route.query.portfolio === 'true' || route.query.virtualPortfolio) {
      showPortfolioVisualization.value = true;
    }
  },
  {
    immediate: true,
  }
);

const unwatchPortfolioSymbols = watch(isFetchingPortfolio, async () => {
  const firstSymbol = portfolioSymbols.value?.sort(
    (a, b) => b.value - a.value
  )?.[0];

  if (
    Boolean(portfolioSymbols.value.length) &&
    firstSymbol &&
    !route.query.isin
  ) {
    await updateActiveIsin(getSymbolIsin(firstSymbol));

    unwatchPortfolioSymbols();
  } else if (
    (isSuccessLoadingPortfolio.value || isErrorLoadingPortfolio.value) &&
    !route.query.isin
  ) {
    await updateActiveIsin(SALAM_ISIN);

    unwatchPortfolioSymbols();
  }
});
</script>

<route lang="yaml">
meta:
  auth: true
  layout: App
  title: common.worthyDocuments
</route>

<style>
.splitpanes.default-theme .splitpanes__pane,
.splitpanes.default-theme .splitpanes__splitter {
  background-color: inherit !important;
}

.splitpanes--vertical > .splitpanes__splitter {
  width: 8px !important;
}

.splitpanes--horizontal > .splitpanes__splitter {
  height: 8px !important;
  margin-top: 0 !important;
}

.splitpanes__splitter {
  border: none !important;
}

.splitpanes__splitter:before,
.splitpanes__splitter:after {
  width: 20px !important;
  background-color: #999999 !important;
}

.disable-last-pane-resize .splitpanes__splitter:nth-last-child(2) {
  pointer-events: none;
}
</style>
